import { AdvertisementApi } from '@mbrtargeting/metatag-shared-types/ad-engine';
import { AdSize, EditFormatOptions, ExtendedFormatSettings, FormatMasterInterface } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { getViewportDimensions, isStringArray } from '@mbrtargeting/metatag-utils';
import { filterSizes, isSingleSizeArray } from '../../../utils/adsize-helper.js';
import { FormatController } from './format-controller.js';

// just some aliases to make usage more readable
type Slotname = string;
type Ident = string;

export class FormatMaster implements FormatMasterInterface {

    private blockedByPublisher: boolean = false;

    constructor(
        private controller: FormatController,
        public formatName: string,
        public settings: ExtendedFormatSettings,
    ) { }

    /**
     * Iterates through the availableOn array from the format and constructs a size array for each slotName found.
     *
     * @param isPhone signal mobile device to generate sizes differently
     * @returns an object containing sizes per slotname where this format contributes to
     */
    public constructSizesPerSlotname(isPhone: boolean): Record<Slotname, AdSize[]> {
        // extract settings we need to use for sizes construction and set their default values
        const { availableOn = [], dfpSizes = [], countSizesUp = false, countFirstSizeUp = false, useOriginalSizeEverywhere = false } = this.settings || {};
        // create a sizes-list per slot
        const sizesForSlot = (slotName: string, slotOccurrenceCount: number): AdSize[] => {
            const blockedSlotSizes: AdSize[] = []; // TODO get blockedSlotSizes from somewhere
            const adjustedFormatSizes: AdSize[] = (dfpSizes as AdSize[]).filter(filterSizes(blockedSlotSizes)); // TODO redefine in config-types

            if (slotOccurrenceCount > 1 && countSizesUp) {
                const secondarySizes: AdSize[] = adjustedFormatSizes.filter(isSingleSizeArray).map(([w, h]) => {
                    return (isPhone || countFirstSizeUp) ? [w + slotOccurrenceCount - 1, h] : [w, h + slotOccurrenceCount - 1];
                });
                return [
                    ...(useOriginalSizeEverywhere ? adjustedFormatSizes : []),
                    ...secondarySizes,
                ];
            }
            return adjustedFormatSizes;
        };

        // for all slotNames listed in `availableOn` create a sizes list
        return availableOn.reduce((result, slotName, index) => ({
            ...result,
            [slotName]: sizesForSlot(slotName, index + 1),
        }), {});
    }

    /**
     * Returns ident for the format if availableOn or blockedPositions array contains the given slotName.
     *
     * @returns ident for the slotname if this format contributes to
     */
    public getIdentForSlotname(slotName: Slotname): Ident | undefined {
        const { availableOn = [], blockedPositions = [], ident } = this.settings || {};
        return [...availableOn, ...blockedPositions].includes(slotName) ? ident : undefined;
    }

    /**
     * Check if a format is active.
     *
     * @returns true if format is active
     */
    public isActive(): boolean {
        return !!this.settings?.active;
    }

    /**
     * Check if the format is blocked blocked by zone with exact or partial match.
     *
     * @param zone the zone to check
     * @returns true if format is blocked
     */
    public isBlockedByZone(zone: string): boolean {
        const { blockedZones = [] } = this.settings || {};
        if (blockedZones.length === 0 || !isStringArray(blockedZones)) return false;
        // if zone match is exact(ends with !), than whole string should match
        if (blockedZones.some(blockedZone => blockedZone.endsWith('!') && zone === blockedZone.slice(0, -1))) return true;
        // if zone match is not exact, then blockedZone string should be included in zone string
        return blockedZones.some(blockedZone => zone.includes(blockedZone));
    }

    /**
     * Check if the format is blocked blocked by viewport.
     *
     * @returns true if format is blocked
     */
    public isBlockedByViewport(): boolean {
        const { blockedViewportBuckets = [] } = this.settings || {};
        const { viewportWidth } = getViewportDimensions();
        const viewportBuckets = this.controller.getViewportBuckets();
        return viewportBuckets
            .filter(({ minWidth, maxWidth }) => minWidth <= viewportWidth && viewportWidth <= maxWidth)
            .some(({ name }) => blockedViewportBuckets.includes(name));
    }

    /**
     * Marks a format as blocked by publisher.
     *
     * @param blockedByPublisher true if format should be blocked
     */
    public setBlockedByPublisher(blockedByPublisher: boolean) {
        this.blockedByPublisher = blockedByPublisher;
    }

    /**
     * Checks if a format is blocked by publisher.
     *
     * @returns true if format is blocked
     */
    public isBlockedByPublisher(adslot?: { getBlockedFormats: () => string[] }): boolean {
        return this.blockedByPublisher || adslot?.getBlockedFormats().includes(this.formatName) || false;
    }

    /**
     * used by Ad Engine to call a function, set by WebDev in local.js of website for given format.
     */
    public startFormat(advertisement: AdvertisementApi, editFormatOptions: EditFormatOptions): void {
        this.settings.startFormat(advertisement, editFormatOptions)
    }

    /**
     * used by Ad Engine to call a function, set by WebDev in local.js of website for given format.
     */
    public finishFormat(advertisement: AdvertisementApi, editFormatOptions: EditFormatOptions): void {
        this.settings.finishFormat(advertisement, editFormatOptions)
    }
}
